<template>
    <v-slide-x-transition mode="out-in">
        <v-container v-if="showComponent" class="mt-5" style="max-width: 600px">
            <CardTitle
                :title="$t('setPassword.title')"
                :link="{ href: '/login', text: $t('resetRequest.backToLogin') }"
            />

            <v-card class="pa-5 mt-2">
                <v-card-text>
                    <v-form ref="form" v-model="formValid">
                        <!-- New password -->
                        <v-text-field
                            v-model="newPassword"
                            :rules="passwordRules"
                            :label="$t('changePassword.newPassword')"
                            :type="showNewPassword ? 'text' : 'password'"
                            :append-inner-icon="
                                showNewPassword ? 'mdi-eye-off' : 'mdi-eye'
                            "
                            @click:append-inner="
                                showNewPassword = !showNewPassword
                            "
                            required
                        />

                        <!-- Confirm password -->
                        <v-text-field
                            v-model="confirmPassword"
                            :rules="confirmPasswordRules"
                            :label="$t('changePassword.confirmNewPassword')"
                            :type="showConfirmPassword ? 'text' : 'password'"
                            :append-inner-icon="
                                showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'
                            "
                            @click:append-inner="
                                showConfirmPassword = !showConfirmPassword
                            "
                            class="mt-2"
                            required
                        />
                    </v-form>
                </v-card-text>

                <v-card-actions class="d-flex align-items-center flex-column">
                    <v-spacer />
                    <v-btn
                        color="info"
                        variant="flat"
                        :disabled="!formValid || saving"
                        @click="setPassword"
                    >
                        {{ $t("setPassword.save") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
            <LoadingOverlay :model-value="saving" />
        </v-container>
    </v-slide-x-transition>
</template>

<script>
import axios from "axios";
import CardTitle from "@/components/registration/CardTitle.vue";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";
import { showAlertModal } from "@/utils";

const COMMON_PASSWORDS = [
    "password",
    "123456",
    "12345678",
    "qwerty",
    "abc123",
    "football",
    "letmein",
    "monkey",
    "111111",
    "1234567",
];

export default {
    name: "ResetPassword",
    components: { CardTitle, LoadingOverlay },

    data() {
        return {
            newPassword: "",
            confirmPassword: "",
            formValid: false,
            showNewPassword: false,
            showConfirmPassword: false,
            saving: false,
            /* --------------------------------------------------------------------
             *  Django‑like password rules
             *  ------------------------------------------------------------------ */
            passwordRules: [
                /* required -------------------------------------------------------- */
                (v) =>
                    !!v ||
                    this.$t("changePassword.passwordRequired") ||
                    "Password is required",

                /* MinimumLengthValidator (>= 8) ----------------------------------- */
                (v) =>
                    (v && v.length >= 8) ||
                    this.$t("changePassword.passwordMinLength") ||
                    "Password must be at least 8 characters",

                /* NumericPasswordValidator (must not be only digits) -------------- */
                (v) =>
                    !/^\d+$/.test(v) ||
                    this.$t("changePassword.passwordOnlyNumbers") ||
                    "Password cannot be entirely numeric",

                /* CommonPasswordValidator ----------------------------------------- */
                (v) =>
                    !COMMON_PASSWORDS.includes(String(v).toLowerCase()) ||
                    this.$t("changePassword.passwordTooCommon") ||
                    "Password is too common",
            ],
            token: "",
            showComponent: false,
        };
    },

    computed: {
        /* confirm‑password rules stay the same */
        confirmPasswordRules() {
            return [
                (v) =>
                    !!v ||
                    this.$t("changePassword.confirmPasswordRequired") ||
                    "Confirm password is required",
                (v) =>
                    v === this.newPassword ||
                    this.$t("changePassword.passwordsMustMatch") ||
                    "Passwords must match",
            ];
        },
    },

    created() {
        this.token = this.$route.query.token || this.$route.params.token || "";
    },

    mounted() {
        this.showComponent = true;
    },

    methods: {
        showAlertModal,
        async setPassword() {
            /* run Vuetify validations first */
            if (!this.$refs.form.validate()) return;

            this.saving = true;
            try {
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/auth/set-password/`,
                    { token: this.token, new_password: this.newPassword }
                );
                this.showAlertModal(
                    this.$store,
                    this.$t("setPassword.successMessage"),
                    "success",
                    5000
                );
                this.$router.push("/login");
            } catch (error) {
                console.error("Error setting password:", error);
                const msg = error?.response?.data?.error || this.$t("setPassword.errorMessage");
                this.showAlertModal(
                    this.$store,
                    msg,
                    "danger",
                    12000,
                    error
                );
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
